import { cloneElement, useEffect, useId, useState, type ReactElement } from 'react'

export type TooltipProps = {
  /** A short description; never the only way to learn what a control does. */
  label: string
  /** The control the tooltip describes; the tooltip joins its aria-describedby. */
  children: ReactElement<Record<string, unknown>>
  position?: 'top' | 'bottom'
}

/**
 * A short description that appears on hover and keyboard focus, stays while
 * the pointer is over it, and closes with Escape (WCAG 1.4.13).
 */
export default function Tooltip({ label, children, position = 'top' }: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <span
      className={`mds-tooltip mds-tooltip--${position}`}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], id].filter(Boolean).join(' '),
      })}
      <span role="tooltip" id={id} className="mds-tooltip__body" hidden={!open}>
        {label}
      </span>
    </span>
  )
}
